import { createSlice } from "@reduxjs/toolkit";
import {
  getDoctorProfile,
  getDoctorSelections,
  getDoctorCategories,
  getDoctorSubcategories,
  createDoctorSelections,
} from "../Thunk/doctorExpertiseThunk";
import { verifyDoctorOtp } from "../Thunk/doctorAuthThunk/doctorAuthThunk";

const initialState = {
  loading: false,
  subcategoriesLoading: false,
  saveLoading: false,
  profileLoading: false,
  error: null,
  successMessage: null,
  categories: [],
  subCategoryMap: {},
  doctorProfile: null,
  existingSelections: [],
};

const doctorExpertiseSlice = createSlice({
  name: "doctorExpertise",
  initialState,
  reducers: {
    clearDoctorExpertiseError: (state) => {
      state.error = null;
    },
    clearDoctorExpertiseSuccess: (state) => {
      state.successMessage = null;
    },
    /** Call on doctor logout so stale profile cannot drive redirects (e.g. to dashboard without a token). */
    resetDoctorSession: (state) => {
      state.doctorProfile = null;
      state.existingSelections = [];
      state.profileLoading = false;
      state.error = null;
      state.successMessage = null;
      state.saveLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifyDoctorOtp.fulfilled, (state) => {
        // Critical: avoid OTP navigation using a *stale* profile from a previous session/route
        // (otherwise status can look like pending_selection and wrongly send users to /Expertise).
        state.doctorProfile = null;
        state.profileLoading = true;
      })
      .addCase(getDoctorProfile.pending, (state) => {
        state.profileLoading = true;
        state.error = null;
      })
      .addCase(getDoctorProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.doctorProfile = action.payload;
      })
      .addCase(getDoctorProfile.rejected, (state) => {
        state.profileLoading = false;
        state.doctorProfile = null;
      })
      .addCase(getDoctorSelections.fulfilled, (state, action) => {
        state.existingSelections = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(getDoctorCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDoctorCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories =
          action.payload?.data ||
          action.payload?.items ||
          action.payload?.categories ||
          action.payload ||
          [];
      })
      .addCase(getDoctorCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getDoctorSubcategories.pending, (state) => {
        state.subcategoriesLoading = true;
        state.error = null;
      })
      .addCase(getDoctorSubcategories.fulfilled, (state, action) => {
        state.subcategoriesLoading = false;
        const { categoryId, data } = action.payload || {};
        if (!categoryId) return;
        state.subCategoryMap[categoryId] =
          data?.data || data?.items || data?.subcategories || data || [];
      })
      .addCase(getDoctorSubcategories.rejected, (state, action) => {
        state.subcategoriesLoading = false;
        state.error = action.payload?.message || action.payload || "Failed to fetch subcategories";
      })
      .addCase(createDoctorSelections.pending, (state) => {
        state.saveLoading = true;
        state.error = null;
      })
      .addCase(createDoctorSelections.fulfilled, (state, action) => {
        state.saveLoading = false;
        state.successMessage =
          action.payload?.message || "Selections saved successfully.";
      })
      .addCase(createDoctorSelections.rejected, (state, action) => {
        state.saveLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearDoctorExpertiseError,
  clearDoctorExpertiseSuccess,
  resetDoctorSession,
} = doctorExpertiseSlice.actions;

export default doctorExpertiseSlice.reducer;
